import { ICONS, DataView, KeyTypes, Loader, Divider, Dropdown, ActionButton } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { OnOptionSelect } from '@creditsights/styleguide-web/components/Dropdown/Dropdown.types';
import { orderBy } from 'lodash';
import { ROUTES } from '../../../../Base.constants';
import SidePanel from '../../../../components/SidePanel/SidePanel';
import useFetchUserPermissions from '../../../../hooks/permissions/useFetchUserPermissions';
import { useDefaultReadingList } from '../../../../hooks/readingList';
import useUpdateReadingList from '../../../../hooks/readingList/useUpdateReadingList';
import { useToast } from '../../../../hooks/toast/useToast';
import { ReadingListArticle } from '../../../../models/ReadingListArticle';
import copyToClipboard from '../../../../utils/clipboardUtils';
import { hasPermissionToShare } from '../../../../utils/permissionsUtils';
import NotFound from '../../../NotFound';
import NoContent from '../../NoContent/NoContent';
import { getKey, viewConfig } from '../ArticlesTab.logic';
import styles from './ArticlesTabContainer.module.scss';

const cx = classNames.bind(styles);

export interface ArticleOutletContext {
  isLoading: boolean;
}

const SORT_OPTIONS: Partial<Record<keyof ReadingListArticle, string>> = {
  bookmarkedAt: 'Recently Saved',
  publishDate: 'Recently Published',
};

interface ArticlesTabContainerProps {
  articles: ReadingListArticle[] | undefined;
  isArticleInInitialList: boolean;
  activeArticlesIds?: number[];
  isAnyArticleLoading: boolean;
}

const ArticlesTabContainer = ({
  articles,
  isArticleInInitialList,
  activeArticlesIds,
  isAnyArticleLoading,
}: ArticlesTabContainerProps): React.ReactElement => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<KeyTypes[]>([]);
  const [selectedSortBy, setSelectedSortBy] = useState(Object.keys(SORT_OPTIONS)[0]);
  const { data: readingList, isLoading: isDefaultReadingListLoading } = useDefaultReadingList();
  const { isLoading: isUnbookmarking, mutate } = useUpdateReadingList();
  const { showToast } = useToast();
  const { data: userPermissions } = useFetchUserPermissions();
  const isLoading = isAnyArticleLoading || isDefaultReadingListLoading;

  const sortedArticles = useMemo(() => {
    return orderBy(articles, selectedSortBy, 'desc');
  }, [selectedSortBy, articles]);

  useEffect(() => {
    const bookmarkedSelectedArticles = selectedItems.filter((selectedItem) =>
      articles?.find((article) => article.articleId === selectedItem)
    );
    if (!isEqual(sortBy(selectedItems), sortBy(bookmarkedSelectedArticles))) {
      setSelectedItems(bookmarkedSelectedArticles);
    }
  }, [articles, selectedItems]);

  const onUnbookmarkClick = () => {
    if (!readingList) {
      return;
    }

    const updatedReadingList = { ...readingList };
    updatedReadingList.articles = readingList.articles.filter((article) => !selectedItems.includes(article.articleId));

    mutate(updatedReadingList, {
      onSuccess: () => {
        setSelectedItems([]);
      },
    });
  };

  const onBulkShareClick = async (): Promise<void> => {
    await copyToClipboard(
      selectedItems.map((articleId) => ({
        link: `${window.location.origin}${ROUTES.article(articleId)}`,
        title: articles?.find((article) => article.articleId === articleId)?.title ?? '',
      }))
    );
    showToast(selectedItems.length > 1 ? 'Links copied to clipboard' : 'Link copied to clipboard');
  };

  if (!articles?.length && !activeArticlesIds?.length && !isLoading) {
    return (
      <NoContent
        dataTestId="no-articles-saved"
        header="No Articles yet"
        infoMessage="When you save articles, they will appear here."
        iconName={ICONS.NoContent}
        buttonParams={{
          ariaLabel: 'Explore Articles',
          title: 'Explore Articles',
          onClick: () => navigate(ROUTES.globalFeed),
        }}
      />
    );
  }

  const canShare = hasPermissionToShare(userPermissions!);
  return (
    <div className={cx('body')}>
      <SidePanel
        noContentMessage={
          !articles?.length && !isLoading
            ? 'You don’t have any saved articles. When you save articles, they will appear here.'
            : undefined
        }
        isLoading={isLoading}
      >
        <DataView
          data={sortedArticles || []}
          getKey={getKey}
          config={viewConfig(cx('saved-date'))}
          classNames={{ dataItemContent: cx('article-item') }}
          activeKeys={activeArticlesIds}
          searchable
          selectable
          searchByFields={['title']}
          selectedItems={selectedItems}
          searchEmptyText="No articles found. Try searching for a valid article title."
          onSelectionChange={setSelectedItems}
          bulkActionButtons={
            <>
              {canShare && (
                <ActionButton
                  icon={ICONS.Share}
                  onClick={onBulkShareClick}
                  tooltip="Share"
                  dataTestId="bulk-share-button"
                  ariaLabel="Share"
                />
              )}
              <ActionButton
                icon={ICONS.BookmarkFilled}
                onClick={() => onUnbookmarkClick()}
                isLoading={isUnbookmarking}
                tooltip="Remove from My Content"
                dataTestId="bulk-unbookmark-button"
                ariaLabel="Unbookmark"
              />
            </>
          }
          rightSideActionButtons={
            <Dropdown
              className={cx('dropdown')}
              dataTestId="sort-select"
              withCaretIcon={false}
              icon={null}
              value={selectedSortBy}
              onOptionSelect={setSelectedSortBy as OnOptionSelect}
              options={Object.keys(SORT_OPTIONS).map((sort) => ({
                value: sort,
                text: SORT_OPTIONS[sort],
                key: sort,
              }))}
              withCheckmarks
              trigger={
                <ActionButton
                  onClick={() => null}
                  icon={ICONS.Sort}
                  tooltip={`Sort by ${SORT_OPTIONS[selectedSortBy]}`}
                  dataTestId="sort-button"
                  ariaLabel="Sort"
                />
              }
            />
          }
        />
        <Divider />
      </SidePanel>
      <div data-testid="right-side-container" className={cx('right-side-container')}>
        {isLoading && !articleId && <Loader position="center" />}
        {isArticleInInitialList || isLoading || !articleId ? (
          <Outlet
            context={{
              isLoading: isLoading || !articleId,
            }}
          />
        ) : (
          <NotFound showNavigateToFeeds={false} />
        )}
      </div>
    </div>
  );
};

export default ArticlesTabContainer;
